import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase-config';

const SignUp = () => {
  const [userDetails, setUserDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  // Extract query parameters
  const projectId = new URLSearchParams(location.search).get('projectId');
  const emailFromLink = decodeURIComponent(new URLSearchParams(location.search).get('email') || '');

  useEffect(() => {
    if (emailFromLink) {
      setUserDetails((prev) => ({
        ...prev,
        email: emailFromLink,
      }));
    } else {
      setError('Invalid sign-up link. Please contact support.');
    }
  }, [emailFromLink]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails({ ...userDetails, [name]: value });
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (userDetails.password !== userDetails.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    const messages = [
      'Creating your account...',
      'Setting up your user profile...',
      'Assigning project ownership...',
      'Redirecting to your client portal...',
    ];

    const displayMessages = async () => {
      for (const message of messages) {
        setLoadingMessage(message);
        await new Promise((resolve) => setTimeout(resolve, 1500)); // Wait 1.5 seconds
      }
    };

    try {
      await displayMessages();

      const userCredential = await createUserWithEmailAndPassword(auth, userDetails.email, userDetails.password);
      const userId = userCredential.user.uid;

      // Save user details to Firestore
      await setDoc(doc(db, 'users', userId), {
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        email: userDetails.email,
        isAdmin: false,
        projectId,
        creationDate: new Date(),
      });

      // Update the project's owner in Firestore
      if (projectId) {
        await setDoc(
          doc(db, 'projects', projectId),
          { owner: userId },
          { merge: true }
        );
      }

      navigate('/client');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '24px' }}>
        <div style={{ width: '32px', height: '32px', border: '3px solid rgba(242,241,237,0.2)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'rgba(242,241,237,0.7)', margin: 0 }}>{loadingMessage}</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 24px' }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <div style={{ marginBottom: '32px' }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-tertiary)', margin: '0 0 8px' }}>Client portal</p>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.75rem,3.5vw,2.5rem)', fontWeight: 700, letterSpacing: '-1.5px', color: 'var(--text-primary)', margin: '0 0 6px', lineHeight: 1.1 }}>Set Up Your Account</h1>
          <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-secondary)', margin: 0 }}>Complete your profile to access your project portal.</p>
        </div>

        <form onSubmit={handleSignUp} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {error && <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', color: 'var(--accent)', margin: 0, padding: '10px 12px', background: 'rgba(245,78,0,0.06)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(245,78,0,0.15)' }}>{error}</p>}

          {/* First + Last name side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>First Name</label>
              <input
                type="text"
                name="firstName"
                value={userDetails.firstName}
                onChange={handleInputChange}
                required
                style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-primary)', background: 'var(--surface-100)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={userDetails.lastName}
                onChange={handleInputChange}
                required
                style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-primary)', background: 'var(--surface-100)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Email (read-only) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Email</label>
            <input
              type="email"
              name="email"
              value={userDetails.email}
              readOnly
              required
              style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-tertiary)', background: 'var(--surface-300)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          {/* Password */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Password</label>
            <input
              type="password"
              name="password"
              value={userDetails.password}
              onChange={handleInputChange}
              required
              style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-primary)', background: 'var(--surface-100)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          {/* Confirm Password */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={userDetails.confirmPassword}
              onChange={handleInputChange}
              required
              style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-primary)', background: 'var(--surface-100)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <button
            type="submit"
            style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', padding: '12px 24px', fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s', marginTop: '8px' }}
          >
            Create Account
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignUp;
